import React from "react";
import { Link } from "react-router-dom";
import { Search, Gavel, Truck, ShieldCheck, Coins, PackageCheck, HandCoins } from "lucide-react";
import { Button } from "../components/ui/button";
import { useBuyerFeePct } from "../lib/useBuyerFee";

export default function HowItWorks() {
  const buyerFee = useBuyerFeePct();
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-16">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">How It Works</div>
      <h1 className="mt-2 text-3xl md:text-5xl font-extrabold">Trust + Transparency, end-to-end.</h1>
      <p className="mt-4 text-slate-600 dark:text-slate-400 max-w-2xl">
        AuctionBulls controls every step from listing to payout so buyers and sellers can transact with confidence.
      </p>

      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <Panel title="For Buyers" points={[
          { icon: Search, t: "Discover", d: "Browse verified auctions across categories and see the exact total before you bid." },
          { icon: Gavel, t: "Bid", d: "Bids are binding and transparent. Anti-sniping keeps auctions fair." },
          { icon: Coins, t: "Pay", d: "Escrow-secured payment. Your funds are only released after you accept the item." },
          { icon: Truck, t: "Receive", d: "Tracked delivery + 3-day inspection window." },
        ]} />
        <Panel title="For Sellers" points={[
          { icon: PackageCheck, t: "List Free", d: "Zero listing fee. Only pay 8% when your auction ends in a successful sale." },
          { icon: Gavel, t: "Auction", d: "Reach serious, verified bidders. Anti-sniping ensures competitive endings." },
          { icon: Truck, t: "Ship", d: "Guided shipping workflow with tracking + optional Pack & Prove evidence." },
          { icon: HandCoins, t: "Get Paid", d: "Payout released after buyer inspection window closes." },
        ]} />
      </div>

      <div className="mt-16 rounded-3xl bg-slate-900 text-white p-8 md:p-12 grid md:grid-cols-4 gap-6">
        <Kpi n="₹0" l="Listing fee" />
        <Kpi n="8%" l="Seller success fee" />
        <Kpi n={`${buyerFee ?? 5}%`} l="Buyer protection" />
        <Kpi n="3 days" l="Inspection window" />
      </div>

      <div className="mt-16 flex flex-wrap gap-3 justify-center">
        <Link to="/auctions"><Button size="lg" className="bg-slate-900 text-white rounded-full px-6">Start Bidding</Button></Link>
        <Link to="/sell"><Button size="lg" variant="outline" className="rounded-full px-6">Start Selling</Button></Link>
      </div>
    </div>
  );
}

function Panel({ title, points }) {
  return (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
      <h3 className="text-xl font-extrabold mb-4">{title}</h3>
      <div className="space-y-4">
        {points.map((p) => (
          <div key={p.t} className="flex gap-4">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center flex-shrink-0"><p.icon className="h-5 w-5" /></div>
            <div>
              <div className="font-bold">{p.t}</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">{p.d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function Kpi({ n, l }) {
  return <div><div className="text-3xl font-extrabold text-amber-400">{n}</div><div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{l}</div></div>;
}
